/* eslint-disable react/no-unused-prop-types */
import React, { createContext, useContext, useMemo, useState } from "react";

interface ModalProviderInitProps {
  children: React.ReactNode;
  openModal?: (modalId: string, props?: {}) => void;
  closeModal?: () => void;
}

const defaultValue = {
  openModal: () => {},
  closeModal: () => {},
};

interface ModalProviderProps extends Omit<ModalProviderInitProps, "children"> {}

interface InitialStateProps {
  modalId: string;
  modalProps: {};
}

const initialState: InitialStateProps = {
  modalId: "",
  modalProps: {},
};

const Context = createContext<InitialStateProps>(initialState);
const UpdateContext = createContext<ModalProviderProps>(defaultValue);

function useModal() {
  const context = useContext(Context);
  if (context === undefined) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
}

function useUpdateModal() {
  const context = useContext(UpdateContext);
  if (context === undefined) {
    throw new Error("useUpdateModal must be used within a ModalProvider");
  }
  return context;
}

const ModalProvider = ({ children }: ModalProviderInitProps) => {
  const [state, setState] = useState(initialState);

  const closeModal = () => {
    setState(initialState);
  };

  const openModal = (modalId: string, modalProps = {}) => {
    setState({
      modalId,
      modalProps,
    });
  };

  const actions = useMemo(
    () => ({
      openModal,
      closeModal,
    }),
    [],
  );

  return (
    <Context.Provider value={state}>
      <UpdateContext.Provider value={actions}>
        {children}
      </UpdateContext.Provider>
    </Context.Provider>
  );
};

export { ModalProvider, useModal, useUpdateModal };
